import {
  Select,
  SelectOptionType,
  TextAreaInput,
  TextInput,
} from '@octano/global-ui';
import { useCallback, useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col } from 'reactstrap';
import {
  checksCourseDependencies,
  deleteTempFile,
  uploadTempFile,
} from '../../../../api/requests/petitions';
import MultipleFileInput from '../../../../components/fileInput/MultipleFileInput';
import { useUserState } from '../../../../hooks/useUserState';
import { useValidations } from '../../../../hooks/useValidations';
import { TranslationsKeys } from '../../../../locales/translations';
import { PetitionData, ViewPetition } from '../../../../types/petitions';
import PetitionFileDownload from './PetitionFileDownload';

export interface PetitionFormSubjectAssignmentProps {
  careerOptions?: SelectOptionType[];
  subjectAssignmentCourses?: {
    studyPlanEnrollmentId: number;
    courses: {
      id: number;
      name: string;
      code: string;
      shortening: string;
      paymentAmount: number | undefined;
    }[];
  }[];
  downloadFiles?: ViewPetition['docs'];
  downloadFileResponse?: ViewPetition['docResponse'];
  onlyView?: boolean;
  isResolved?: boolean;
  onLoadingChange?: (loading: boolean) => void;
}

export default function PetitionFormSubjectAssignment({
  careerOptions = [],
  subjectAssignmentCourses = [],
  onlyView = false,
  isResolved = false,
  downloadFiles,
  downloadFileResponse,
  onLoadingChange,
}: PetitionFormSubjectAssignmentProps) {
  const { t } = useTranslation(TranslationsKeys.PETITIONS);
  const maxValidation = useCallback(
    (value: number) => ({
      value,
      message: t(`modal.form.validation.max`, { value }),
    }),
    [t],
  );

  const { userData } = useUserState();

  const {
    validateTextNotEmpty,
    validatePhone,
    validateEmail,
  } = useValidations();
  const { control, setValue } = useFormContext<PetitionData>();

  const text = useMemo(() => {
    const prefix = 'modal.form';
    return {
      studyPlan: t(`${prefix}.studyPlan`),
      phone: t(`${prefix}.phone`),
      email: t(`${prefix}.email`),
      title: t(`${prefix}.title`),
      reason: {
        label: t(`${prefix}.reason.label`),
        placeholder: t(`${prefix}.reason.placeholder`),
      },
      courses: {
        label: t(`${prefix}.courses.label`),
        placeholder: t(`${prefix}.courses.placeholder`),
        required: t(`${prefix}.courses.required`),
      },
      file: {
        label: t(`${prefix}.file.label`),
        button: t(`${prefix}.file.button`),
      },
      download: {
        labelResponse: t(`${prefix}.download.labelResponse`),
        label: t(`${prefix}.download.label`),
        button: t(`${prefix}.download.button`),
      },
      validation: {
        select: t(`${prefix}.validation.select`),
      },
      resolved: {
        label: t(`${prefix}.resolved.label`),
      },
    };
  }, [t]);

  const selectedStudyPlanEnrollment = useWatch({
    name: 'studyPlanEnrollment',
    control,
  });
  const course = useWatch<PetitionData>({
    name: 'course',
    control,
  });

  const courseOptions = useMemo<SelectOptionType[]>(() => {
    const selectedId =
      selectedStudyPlanEnrollment?.value ?? selectedStudyPlanEnrollment;

    const matchingCourses =
      subjectAssignmentCourses.find(
        (entry) => entry.studyPlanEnrollmentId === selectedId,
      )?.courses ?? [];

    return matchingCourses.map((course) => ({
      value: course.id,
      label: course.name,
    }));
  }, [selectedStudyPlanEnrollment, subjectAssignmentCourses]);

  useEffect(() => {
    if (course?.label) {
      setValue('title', `Adición de asignatura: ${course.label}`);
    }
  }, [course, setValue]);

  const validateCourseDependencies = useCallback(
    async (course: SelectOptionType) => {
      if (!course?.value || !userData?.id || !selectedStudyPlanEnrollment) {
        return;
      }

      const response = await checksCourseDependencies({
        courseId: course.value,
        studyPlanEnrollmentId: selectedStudyPlanEnrollment.value,
        accountId: userData.id,
      });

      if (response.data) {
        return undefined;
      } else if (response.error) {
        return t(`modal.form.validation.courseDependecy`);
      }
    },
    [t, userData?.id, selectedStudyPlanEnrollment],
  );

  return (
    <>
      <Col md={12}>
        <Select
          label={text.studyPlan}
          control={control}
          disabled={onlyView}
          options={careerOptions}
          rules={{
            required: text.validation.select,
          }}
          name="studyPlanEnrollment"
          isClearable={false}
        />
      </Col>
      <Col md={6} className="my-3">
        <TextInput
          label={text.phone}
          name="cellPhone"
          disabled={onlyView}
          control={control}
          rules={{
            validate: { validatePhone, require: validateTextNotEmpty },
          }}
        />
      </Col>
      <Col md={6} className="my-3">
        <TextInput
          label={text.email}
          name="email"
          control={control}
          disabled={onlyView}
          rules={{
            validate: { validateEmail, require: validateTextNotEmpty },
          }}
        />
      </Col>
      <Col md={12} className="my-3">
        <Select
          label={text.courses.label}
          placeholder={text.courses.placeholder}
          name="course"
          control={control}
          options={courseOptions}
          disabled={onlyView}
          rules={{
            validate: {
              require: (value) => {
                if (value) {
                  return validateCourseDependencies(value);
                }
                return text.courses.required;
              },
            },
          }}
          isClearable={false}
        />
      </Col>

      <Col md={12}>
        <TextAreaInput
          label={text.reason.label}
          name="description"
          placeholder={text.reason.placeholder}
          disabled={onlyView}
          rules={{
            validate: { require: validateTextNotEmpty },
            maxLength: maxValidation(500),
          }}
          control={control}
        />
      </Col>
      <Col md={12} className="my-4">
        {onlyView ? (
          <PetitionFileDownload
            text={text.download.button}
            label={text.download.label}
            files={downloadFiles}
          />
        ) : (
          <MultipleFileInput
            name="docs"
            label={text.file.label}
            accept="image/png, image/jpeg, application/pdf"
            control={control}
            uploadTempFile={uploadTempFile}
            deleteTempFile={deleteTempFile}
            onLoadingChange={onLoadingChange}
          />
        )}
      </Col>

      {isResolved && downloadFileResponse && (
        <Col md={12} className="my-4">
          <PetitionFileDownload
            text={text.download.button}
            label={text.download.labelResponse}
            file={downloadFileResponse}
          />
        </Col>
      )}
    </>
  );
}
